// Localization is based on '@ngx-translate/core';
// Please be familiar with official documentations first => https://github.com/ngx-translate/core

import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { setActiveLang } from 'src/app/utils/locale';

export interface Locale {
  lang: string;
  data: any;
}

const LOCALIZATION_LOCAL_STORAGE_KEY = 'language';

/** Idiomas que el usuario puede elegir a mano (selector del topbar/sidebar). */
const SUPPORTED_LANGS = ['es', 'ca', 'en'];

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  // Private properties
  private langIds: any = [];

  constructor(private translate: TranslateService) {
    // add new langIds to the list
    this.translate.addLangs(['en']);

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');

    // <html lang> sigue al idioma de la interfaz (lectores de pantalla,
    // guionado, controles nativos de fecha). index.html trae "en" fijo.
    // Las utilidades puras de formato (utils/locale.ts) también lo siguen.
    this.translate.onLangChange.subscribe(({ lang }) => {
      document.documentElement.lang = lang;
      setActiveLang(lang);
    });
  }

  loadTranslations(...args: Locale[]): void {
    const locales = [...args];

    locales.forEach((locale) => {
      // use setTranslation() with the third argument set to true
      // to append translations instead of replacing them
      this.translate.setTranslation(locale.lang, locale.data, true);
      this.langIds.push(locale.lang);
    });

    // add new languages to the list
    this.translate.addLangs(this.langIds);
    this.translate.use(this.getSelectedLanguage());
  }

  setLanguage(lang: string) {
    if (lang) {
      const changed = lang !== this.translate.currentLang;
      this.translate.use(this.translate.getDefaultLang());
      this.translate.use(lang);
      localStorage.setItem(LOCALIZATION_LOCAL_STORAGE_KEY, lang);
      // LOCALE_ID (formatos de `| date`, `| number`, `| currency`…, ver
      // app.module.ts) y los textos calculados una vez con `instant` (columnas
      // de DataTables, opciones de filtros…) solo se fijan al arrancar: al
      // cambiar de idioma se recarga para que todo salga en el idioma nuevo.
      if (changed) document.location.reload();
    }
  }

  /**
   * Returns selected language: la elegida a mano (persistida en localStorage)
   * tiene siempre prioridad; si el usuario nunca eligió ninguna, se detecta el
   * idioma del navegador (`navigator.languages`) entre los soportados, y si
   * ninguno coincide se cae al idioma por defecto de ngx-translate.
   */
  getSelectedLanguage(): any {
    const saved = localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY);
    if (saved) return saved;
    return this.detectBrowserLanguage() ?? this.translate.getDefaultLang();
  }

  /** Primer idioma de `navigator.languages`/`navigator.language` que esté soportado, o `null`. */
  private detectBrowserLanguage(): string | null {
    const candidates = (navigator.languages?.length ? navigator.languages : [navigator.language]) || [];
    for (const candidate of candidates) {
      const short = candidate?.split('-')[0]?.toLowerCase();
      if (short && SUPPORTED_LANGS.includes(short)) return short;
    }
    return null;
  }
}
